// angular
import { Title } from '@angular/platform-browser';
import { ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { BreadcrumbsComponent } from '@app/core/components/breadcrumbs/breadcrumbs.component';
import { OptionsMenuComponent } from '@app/core/components/options-menu/options-menu.component';
import { BarcodeDialogComponent } from '@app/core/dialogs/barcode-dialog/barcode-dialog.component';
import { GalleryComponent } from '@app/core/components/gallery/gallery.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
import { TenderDialogComponent } from '@app/core/dialogs/tender-dialog/tender-dialog.component';
import { TendersDetailSkeletonComponent } from './components/tenders-detail-skeleton/tenders-detail-skeleton.component';
import { TenderMetaCardComponent } from '@tenders/shared/components/tender-meta-card/tender-meta-card.component';
import { TenderInfoCardComponent } from './components/tender-info-card/tender-info-card.component';
import { TenderProposalPanelComponent } from './components/tender-proposal-panel/tender-proposal-panel.component';
import { TenderSupportCardComponent } from './components/tender-support-card/tender-support-card.component';
import { TenderGuidelinesSectionComponent } from './components/tender-guidelines-section/tender-guidelines-section.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
import { TendersService } from '../shared/services/tenders.service';
import { UserService } from '@app/shared/services/user.service';
// interfaces & enums & env
import { IMedia } from '@app/shared/interfaces/media.interface';
import { ITender } from '../shared/interfaces/tender.interface';
import { TenderStatesEnum } from '../shared/enums/tender-states.enum';
import { Roles } from '@app/shared/enums/roles.enum';
import { environment } from '@environments/environment';
import { IBreadcrumbs } from '@app/shared/interfaces/breadcrumbs.interface';
import { PreviewMenuOptionsType } from '@app/+products/shared/types/product-preview.type';
// pipes & directives
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { HasAccessDirective } from '@app/shared/directives/has-access.directive';

@Component({
  selector: 'pwj-tenders-detail',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbsComponent,
    OptionsMenuComponent,
    TranslateModule,
    TranslateDataPipe,
    GalleryComponent,
    ButtonComponent,
    TendersDetailSkeletonComponent,
    TenderMetaCardComponent,
    TenderInfoCardComponent,
    TenderProposalPanelComponent,
    TenderSupportCardComponent,
    TenderGuidelinesSectionComponent,
    HasAccessDirective,
  ],
  templateUrl: './tenders-detail.component.html',
  styleUrl: './tenders-detail.component.scss',
})
export class TendersDetailComponent {
  public panelUrl: string = environment.baseUrl.panelUrl;
  public currentLang!: string;
  public tenderStates = TenderStatesEnum;
  public tenderData!: ITender;
  public isTenderLoading = true;
  public breadcrumbsData: IBreadcrumbs[] = [];
  public menuOptions: PreviewMenuOptionsType[] = [];
  public roles = Roles;
  public _tenderId!: string;

  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _tendersService = inject(TendersService);
  private readonly _dialogService = inject(DialogService);
  private readonly _cd = inject(ChangeDetectorRef);
  private readonly _titleService = inject(Title);
  private readonly _translateService = inject(TranslateService);
  private readonly _translateData = inject(TranslateDataPipe);
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _userService = inject(UserService);

  constructor() {
    this.currentLang = this._translateService.currentLang;

    this._translateService.onLangChange.subscribe(data => {
      this.currentLang = data.lang;
      this.setBreadcrumbs();
    });

    this._activatedRoute.params.subscribe(params => {
      this._tenderId = params?.['tenderId'];
      this._tendersService.getTenderDetails(this._tenderId);
    });

    effect(() => {
      const data = this._tendersService.tenderDetails();
      this.setTenderData(data);
      this._cd.detectChanges();
    });

    effect(() => {
      this.isTenderLoading = this._tendersService.isTenderDetailsLoading();
    });

    effect(() => {
      this._userService.userSignal();
      if (this.tenderData?._id) {
        this.setMenuOptions();
      }
    });
  }

  get stateLabelKey(): string {
    switch (this.tenderData?.state) {
      case TenderStatesEnum.In_The_Future:
        return 'tenders.stateFuture';
      case TenderStatesEnum.Expired:
        return 'tenders.stateExpired';
      default:
        return 'tenders.openTenderBidPhase';
    }
  }

  get requestVolumeLabel(): string {
    if (!this.tenderData?.amount?.value) {
      return '-';
    }

    const unit = this.tenderData.amount.unit
      ? this._translateService.instant(
          `enums.measurements.${this.tenderData.amount.unit}`
        )
      : '';

    return `${this.tenderData.amount.value} ${unit}`.trim().toUpperCase();
  }

  get targetLocationLabel(): string {
    return (
      this._translateData.transform(this.tenderData?.delivery_city?.name) ||
      this._translateData.transform(this.tenderData?.delivery_address) ||
      '-'
    );
  }

  public setTenderData(data: ITender): void {
    if (!data?._id) {
      return;
    }

    const { photos, videos, ...rest } = data;
    const media: IMedia[] = [];

    if (photos?.length) {
      media.push(...photos);
    }
    if (videos?.length) {
      media.push(...videos);
    }

    this.tenderData = {
      ...rest,
      media,
    };

    this.setBreadcrumbs();
    this.setMenuOptions();

    const pageTitle = `${this._translateService.instant('global.tender')} ${this._translateData.transform(data?.title) || ''}`;
    this.setPageTitle(pageTitle);
  }

  public submitBid(): void {
    const dialogRef = this._dialogService.open(TenderDialogComponent, {
      width: 'none',
      height: '36em',
      data: {
        title: 'tenders.participateInTender',
      },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this._tendersService.submitProposalsData(this._tenderId, res);
      }
    });
  }

  public setMenuOptions(): void {
    const options: PreviewMenuOptionsType[] = [
      {
        id: 1,
        name: 'global.share',
        optionClasses:
          '!flex-row menu-options__item--icon-only menu-options__item--header-action',
        icon: 'share',
        onSelectOption: (barcodeContent: string) => {
          this.openQrCodeDialog(barcodeContent);
        },
      },
    ];

    if (
      this._hasAccessPipe.transform([
        Roles.CreateAuctionAll,
        Roles.CreateAuctionSelf,
      ])
    ) {
      options.push({
        id: 2,
        name: 'auctions.createNewAuction',
        optionClasses:
          '!flex-row menu-options__item--icon-only menu-options__item--header-action',
        icon: 'add',
        onSelectOption: () => {
          window.open(
            `${this.panelUrl}/auctions/create`,
            '_blank',
            'noopener,noreferrer'
          );
        },
      });
    }

    if (
      this._hasAccessPipe.transform([
        Roles.CreateTenderAll,
        Roles.CreateTenderSelf,
      ])
    ) {
      options.push({
        id: 3,
        name: 'tenders.createNewTender',
        optionClasses:
          '!flex-row menu-options__item--icon-only menu-options__item--header-action',
        icon: 'add',
        onSelectOption: () => {
          window.open(
            `${this.panelUrl}/tenders/create`,
            '_blank',
            'noopener,noreferrer'
          );
        },
      });
    }

    this.menuOptions = options;
  }

  public setPageTitle(content?: string): void {
    if (content) {
      this._titleService.setTitle(content);
    }
  }

  private openQrCodeDialog(barcodeContent: string): void {
    this._dialogService.open(BarcodeDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'products.qrCode',
        productTitle: this._translateData.transform(this.tenderData?.title),
        productCode: this.tenderData?.code,
        barcodeContent,
      },
    });
  }

  private setBreadcrumbs(): void {
    const tenderTitle =
      this._translateData.transform(this.tenderData?.title) ||
      this._translateService.instant('routes.tenderDetails');

    const roomLabel = this.tenderData?.code
      ? `${this._translateService.instant('tenders.tenderRoom')} #${this.tenderData.code}`
      : tenderTitle;

    this.breadcrumbsData = [
      {
        id: 1,
        breadCrumbTitle: 'routes.home',
        breadCrumbUrl: '/',
      },
      {
        id: 2,
        breadCrumbTitle: 'tenders.activeTenders',
        breadCrumbUrl: '/tenders',
      },
      {
        id: 3,
        breadCrumbTitle: roomLabel,
      },
    ];
  }
}
